import { useEffect } from 'react';
import { Form, Input, Modal, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

type AuditFormValues = {
    auditBy?: string;
    auditRemark?: string;
    memo?: string;
};

type Props = {
    open: boolean;
    mode: 'batch' | 'period';
    count?: number;
    periodLabel?: string;
    loading?: boolean;
    onCancel: () => void;
    onSubmit: (values: AuditFormValues) => void;
};

const AuditModal: React.FC<Props> = ({
    open,
    mode,
    count = 0,
    periodLabel,
    loading,
    onCancel,
    onSubmit,
}) => {
    const [form] = Form.useForm<AuditFormValues>();
    const { formatMessage } = useIntl();

    useEffect(() => {
        if (open) {
            form.resetFields();
        }
    }, [open, mode, form]);

    const isPeriod = mode === 'period';

    return (
        <Modal
            open={open}
            title={
                isPeriod ? (
                    <FormattedMessage id="miningWeeklyRewards.audit.approvePeriodTitle" />
                ) : (
                    <FormattedMessage
                        id="miningWeeklyRewards.audit.approveTitle"
                        values={{ count }}
                    />
                )
            }
            destroyOnClose
            confirmLoading={loading}
            okText={formatMessage({ id: 'miningWeeklyRewards.actions.release' })}
            cancelText={formatMessage({ id: 'common.cancel' })}
            onCancel={onCancel}
            onOk={() => form.submit()}
        >
            {isPeriod ? (
                <Typography.Paragraph>
                    <FormattedMessage
                        id="miningWeeklyRewards.audit.approvePeriodContent"
                        values={{ range: periodLabel || '-' }}
                    />
                </Typography.Paragraph>
            ) : (
                <Typography.Paragraph type="secondary">
                    <FormattedMessage id="miningWeeklyRewards.audit.rewardHint" />
                </Typography.Paragraph>
            )}
            <Form form={form} layout="vertical" onFinish={onSubmit}>
                {isPeriod ? (
                    <>
                        <Form.Item
                            name="auditBy"
                            label={<FormattedMessage id="miningWeeklyRewards.columns.auditBy" />}
                        >
                            <Input
                                allowClear
                                placeholder={formatMessage({
                                    id: 'miningWeeklyRewards.audit.auditByPlaceholder',
                                })}
                            />
                        </Form.Item>
                        <Form.Item
                            name="auditRemark"
                            label={<FormattedMessage id="miningWeeklyRewards.columns.auditRemark" />}
                        >
                            <Input.TextArea
                                rows={3}
                                placeholder={formatMessage({
                                    id: 'miningWeeklyRewards.audit.auditRemarkPlaceholder',
                                })}
                            />
                        </Form.Item>
                    </>
                ) : (
                    <Form.Item
                        name="memo"
                        label={<FormattedMessage id="miningWeeklyRewards.audit.memo" />}
                    >
                        <Input.TextArea
                            rows={3}
                            placeholder={formatMessage({
                                id: 'miningWeeklyRewards.audit.memoPlaceholder',
                            })}
                        />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

export default AuditModal;
